import { __, closeToast, toastError, toastWarning } from '../utils';
import type { SnackbarKey } from 'notistack';
import React from 'react';

export type GeoLocation = [number, number];

type LocationStatus = 'CHECK' | 'DENY' | 'UNAVAILABLE' | 'AVAILABLE';

// Presnú polohu pýtame prvým pokusom, ale na desktope ju nie je z čoho zložiť:
// Mac nemá GPS a polohu skladá z okolitých Wi-Fi sietí, takže cez VPN, na
// Ethernete alebo s vypnutou Wi-Fi vráti CoreLocation `kCLErrorLocationUnknown`
// a k nám príde `POSITION_UNAVAILABLE`. Druhý pokus preto presnosť nežiada,
// dá systému viac času a pustí aj pár minút starú polohu - na vycentrovanie
// mapy aj na polohu podnetu to stačí a inak by používateľ nemal nič.
const PRIMARY_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  // Čerstvejšie než minúta sa na mape aj na podnete správa rovnako ako nová
  // poloha; `0` tu znamenalo, že aj práve zmeraná poloha sa musela merať znova.
  maximumAge: 60000,
};

const FALLBACK_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 20000,
  maximumAge: 300000,
};

type GetPositionOptions = {
  /**
   * Žiadosť, ktorú nespustil používateľ - mapa si polohu pýta sama pri
   * načítaní. Neúspech vtedy **nehlási toastom**, len prepne `locationStatus`.
   * Kým to tak nebolo, človek so zakázanou polohou dostal trvalé žlté
   * upozornenie na každej stránke s mapou, vrátane filtra v administrácii,
   * kde polohu nepotrebuje - a chýbajúcu polohu aj tak hlási indikátor
   * pri mape.
   */
  silent?: boolean;
};

type GeoLocationContextType = {
  getCurrentPosition: (
    options?: GetPositionOptions,
  ) => Promise<GeoLocation | undefined>;
  locationStatus: LocationStatus;
};

const GeoLocationContext =
  React.createContext<GeoLocationContextType>(undefined);

const GeoLocationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [locationStatus, setLocationStatus] =
    React.useState<LocationStatus>('CHECK');

  // Toast o zakázanej polohe je trvalý (persist), takže by po povolení tvrdil
  // niečo, čo už neplatí - držíme si jeho kľúč, aby sme ho vedeli zavrieť.
  const denyToastKeyRef = React.useRef<SnackbarKey | null>(null);

  // Povolenie polohy sa dá v prehliadači zmeniť bez reloadu stránky. Stav si
  // inak držíme len z výsledkov getCurrentPosition, takže z 'DENY' by sa už
  // nepohol - indikátor chýbajúcej polohy by na mape visel až do refreshu.
  React.useEffect(() => {
    if (!navigator.permissions?.query) {
      return;
    }

    let permission: PermissionStatus | null = null;

    const handleChange = () => {
      const isDenied = permission?.state === 'denied';

      if (!isDenied && denyToastKeyRef.current !== null) {
        closeToast(denyToastKeyRef.current);
        denyToastKeyRef.current = null;
      }

      // Po povolení sa vraciame na 'CHECK', aby ďalší getCurrentPosition polohu
      // znova skúsil získať; pri odobraní naopak indikátor hneď ukážeme.
      setLocationStatus(isDenied ? 'DENY' : 'CHECK');
    };

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        permission = result;
        result.addEventListener('change', handleChange);
      })
      // Safari 'geolocation' v permissions.query nepozná - tam ostáva pôvodné
      // správanie, teda stav sa obnoví až pri ďalšom pokuse o získanie polohy.
      .catch(() => undefined);

    return () => permission?.removeEventListener('change', handleChange);
  }, []);

  const getCurrentPosition = React.useCallback(
    ({ silent = false }: GetPositionOptions = {}) => {
      return new Promise<GeoLocation | undefined>((resolve) => {
        // Zo stavu 'UNAVAILABLE' sa tu zámerne **nevraciame prázdni**: je to
        // dočasné zlyhanie systému (Wi-Fi, VPN), nie rozhodnutie používateľa,
        // takže ďalší pokus môže dopadnúť inak. Blokuje len 'DENY' - tam by
        // opakovanie nič nezískalo a prehliadač sa už znova nepýta.
        if (locationStatus === 'DENY') {
          return resolve(undefined);
        }

        if (!navigator.geolocation) {
          setLocationStatus('UNAVAILABLE');
          if (!silent) {
            toastError(
              __('Váš prehliadač nepodporuje získavanie aktuálnej polohy.'),
            );
          }
          return resolve(undefined);
        }

        const handleSuccess: PositionCallback = (position) => {
          setLocationStatus('AVAILABLE');
          const location: GeoLocation = [
            position.coords.longitude,
            position.coords.latitude,
          ];
          resolve(location);
        };

        const handleError: PositionErrorCallback = (err) => {
          if (err.code === err.POSITION_UNAVAILABLE) {
            setLocationStatus('UNAVAILABLE');

            if (!silent) {
              toastWarning(
                __('Nepodarilo sa aktualizovať vašu aktuálnu polohu.'),
              );
            }
          } else if (silent) {
            setLocationStatus(
              err.code === err.PERMISSION_DENIED ? 'DENY' : 'UNAVAILABLE',
            );
          } else {
            if (err.code === err.PERMISSION_DENIED) {
              setLocationStatus('DENY');

              denyToastKeyRef.current = toastWarning(
                __(
                  'Máte zakázané zdieľať svoju aktuálnu polohu. Prosím povoľte prístup k polohe v nastaveniach prehliadača.',
                ),
                true,
              );
            } else {
              setLocationStatus('UNAVAILABLE');

              toastError(__('Nepodarilo sa získať vašu aktuálnu polohu.'));
            }
          }
          resolve(undefined);
        };

        navigator.geolocation.getCurrentPosition(
          handleSuccess,
          (err) => {
            // Druhý pokus má zmysel len tam, kde mohli byť priveľké nároky:
            // zamietnuté povolenie nižšou presnosťou nepovolíš a prehliadač sa
            // druhýkrát ani nepýta, takže sa dialóg neopakuje.
            if (
              err.code === err.POSITION_UNAVAILABLE ||
              err.code === err.TIMEOUT
            ) {
              navigator.geolocation.getCurrentPosition(
                handleSuccess,
                handleError,
                FALLBACK_OPTIONS,
              );
              return;
            }

            handleError(err);
          },
          PRIMARY_OPTIONS,
        );
      });
    },
    [locationStatus],
  );

  return (
    <GeoLocationContext.Provider
      value={{
        getCurrentPosition,
        locationStatus,
      }}
    >
      {children}
    </GeoLocationContext.Provider>
  );
};

export default GeoLocationProvider;

export const useGeoLocation = () => {
  const context = React.useContext(GeoLocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationContext');
  }
  return context;
};
